import { useCallback, useEffect, useRef, useState } from "react";
import { VERSIONS, audioUrl } from "@/lib/enhanceApi";

/**
 * Synced A/B/C player: every ready version plays at once on one clock, only the chosen one is heard,
 * so switching keeps your place in the song (12 ms fade, no click).
 */
export default function useCompare(item) {
  const ctx = useRef(null);
  const live = useRef(null);
  const urls = useRef({});
  const offset = useRef(0);
  const [buffers, setBuffers] = useState({});
  const [failed, setFailed] = useState(false);
  const [active, setActiveState] = useState("polished");
  const activeRef = useRef(active);
  const chosen = useRef(false);
  const bufRef = useRef(buffers);
  bufRef.current = buffers;
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const getCtx = useCallback(() => ctx.current || (ctx.current = new (window.AudioContext || window.webkitAudioContext)()), []);
  const duration = Math.max(0, ...Object.values(buffers).map((b) => b.duration));

  useEffect(() => {
    if (!item) return;
    VERSIONS.forEach(({ key }) => {
      if (!item.versions?.[key]) return;
      const url = audioUrl(item, key, "mp3");
      if (urls.current[key] === url) return;
      urls.current[key] = url;
      fetch(url, { credentials: "include" })
        .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.arrayBuffer(); })
        .then((ab) => getCtx().decodeAudioData(ab))
        .then((buf) => { if (urls.current[key] === url) { setBuffers((b) => ({ ...b, [key]: buf })); setFailed(false); } })
        .catch(() => { urls.current[key] = null; setFailed(true); });
    });
  }, [item, getCtx]);

  const stopNodes = useCallback(() => {
    live.current?.nodes.forEach((n) => { try { n.src.stop(); } catch { /* never started */ } n.src.disconnect(); });
    live.current = null;
  }, []);
  const now = useCallback(() => (live.current ? getCtx().currentTime - live.current.startedAt : offset.current), [getCtx]);
  const start = useCallback((at) => {
    const c = getCtx();
    c.resume();
    stopNodes();
    const nodes = Object.entries(bufRef.current).map(([k, buf]) => {
      const src = c.createBufferSource();
      src.buffer = buf;
      const g = c.createGain();
      g.gain.value = k === activeRef.current ? 1 : 0;
      src.connect(g).connect(c.destination);
      if (at < buf.duration) src.start(0, at);
      return { k, src, g };
    });
    live.current = { nodes, startedAt: c.currentTime - at };
    setPlaying(true);
  }, [getCtx, stopNodes]);
  const pause = useCallback(() => {
    offset.current = now();
    stopNodes();
    setPlaying(false);
    setPos(offset.current);
  }, [now, stopNodes]);
  const toggle = () => (live.current ? pause() : start(offset.current >= duration - 0.05 ? 0 : offset.current));
  const seek = (t) => { offset.current = t; setPos(t); if (live.current) start(t); };
  const select = useCallback((k, byUser = true) => {
    if (byUser) chosen.current = true;
    activeRef.current = k;
    setActiveState(k);
    live.current?.nodes.forEach((n) => n.g.gain.setTargetAtTime(n.k === k ? 1 : 0, getCtx().currentTime, 0.012));
  }, [getCtx]);
  // Hear the best version that's ready (Polished once it exists, unless you picked one yourself).
  useEffect(() => {
    const ready = (k) => Boolean(item?.versions?.[k]);
    const best = ["polished", "natural", "original"].find(ready);
    if (best && (!ready(activeRef.current) || (!chosen.current && best !== activeRef.current))) select(best, false);
  }, [item, select]);

  useEffect(() => { if (live.current) start(now()); }, [buffers, start, now]);
  useEffect(() => {
    if (!playing) return undefined;
    let raf;
    const tick = () => {
      const t = now();
      if (t >= duration) { offset.current = 0; stopNodes(); setPlaying(false); setPos(0); return; }
      setPos(t);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, duration, now, stopNodes]);
  useEffect(() => () => { stopNodes(); ctx.current?.close(); }, [stopNodes]);

  return { buffers, failed, active, select, playing, toggle, pause, seek, pos, duration };
}
